import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import type {
  ApiResponse,
  Team,
  TeamStats,
  CreateTeamRequest,
  TeamLiveWallEntry,
  PublicationControlProjection,
  TeamLivePosition,
  TeamMemberRole,
  TeamRecordingsFilters,
  TeamRecordingsResponse,
} from "@/types";

export function createTeamsApi({ client }: HttpClient) {
  return {
    async getTeams(): Promise<Team[]> {
      const response: AxiosResponse<{
        success: boolean;
        data: Team[];
        total: number;
      }> = await client.get("/teams");
      return response.data.data;
    },

    async getTeam(id: string): Promise<Team> {
      const response: AxiosResponse<ApiResponse<Team>> =
        await client.get(`/teams/${id}`);
      return response.data.data;
    },

    async createTeam(teamData: CreateTeamRequest): Promise<Team> {
      const response: AxiosResponse<ApiResponse<Team>> =
        await client.post("/teams", teamData);
      return response.data.data;
    },

    async updateTeam(
      id: string,
      teamData: Partial<CreateTeamRequest>,
    ): Promise<Team> {
      const response: AxiosResponse<ApiResponse<Team>> =
        await client.put(`/teams/${id}`, teamData);
      return response.data.data;
    },

    async deleteTeam(id: string): Promise<void> {
      await client.delete(`/teams/${id}`);
    },

    async getTeamLiveWall(teamId: string): Promise<TeamLiveWallEntry[]> {
      const response: AxiosResponse<ApiResponse<TeamLiveWallEntry[]>> =
        await client.get(`/teams/${teamId}/live-wall`);
      return response.data.data;
    },

    async setDevicePublicationControl(
      teamId: string,
      deviceId: string,
      on: boolean,
      record = false,
    ): Promise<PublicationControlProjection> {
      const response: AxiosResponse<ApiResponse<{ publicationControl: PublicationControlProjection }>> =
        await client.put(
          `/teams/${teamId}/devices/${deviceId}/publication-control`,
          { on, record },
        );
      return response.data.data.publicationControl;
    },

    async addTeamMember(
      teamId: string,
      data: { userId: string; role: TeamMemberRole },
    ): Promise<Team> {
      const response: AxiosResponse<ApiResponse<Team>> = await client.post(
        `/teams/${teamId}/members`,
        data,
      );
      return response.data.data;
    },

    async removeTeamMember(teamId: string, userId: string): Promise<void> {
      await client.delete(`/teams/${teamId}/members/${userId}`);
    },

    async getTeamLivePositions(teamId: string): Promise<TeamLivePosition[]> {
      const response: AxiosResponse<ApiResponse<TeamLivePosition[]>> =
        await client.get(`/teams/${teamId}/live-wall/positions`);
      return response.data.data;
    },

    async getTeamStats(teamId: string): Promise<TeamStats> {
      const response: AxiosResponse<ApiResponse<TeamStats>> =
        await client.get(`/teams/${teamId}/stats`);
      return response.data.data;
    },

    async getTeamRecordings(
      teamId: string,
      filters?: TeamRecordingsFilters,
    ): Promise<TeamRecordingsResponse> {
      const params = Object.fromEntries(
        Object.entries(filters ?? {}).filter(
          ([, value]) => value !== undefined && value !== "",
        ),
      );
      const response: AxiosResponse<ApiResponse<TeamRecordingsResponse>> =
        await client.get(`/teams/${teamId}/recordings`, { params });
      return response.data.data;
    },
  };
}
